import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import { glassDialogPaperProps } from '../styles/tokens';
import StatCard from './ui/StatCard';
import { BOX_CAPACITY } from '../config';

const RemainingSpaceModal = ({
  open,
  onClose,
  remainingSpaceData,
  stats,
  loading,
  error,
  onRefresh
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: glassDialogPaperProps.className,
        sx: {
          ...glassDialogPaperProps.sx,
          transition: 'background 0.3s, box-shadow 0.3s',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          fontWeight: 'bold',
          bgcolor: 'transparent',
          color: theme => theme.palette.text.primary
        }}
      >
        Remaining Box Space ({BOX_CAPACITY} Vials/Box)
      </DialogTitle>
      <DialogContent 
        dividers 
        sx={{ 
          bgcolor: 'transparent',
          p: 3 
        }}
      >
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography variant="subtitle1" sx={{ ml: 2 }}>Loading remaining space data...</Typography>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        {!loading && !error && (
          <>
            {stats && stats.summary && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Inventory Summary
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  <StatCard label="Total Records" value={stats.summary.total_records ?? 0} icon="fa-vials" tone="primary" />
                  <StatCard label="Total Vials" value={stats.summary.total_vials ?? 0} icon="fa-flask" tone="sage" />
                  <StatCard label="Total Cells" value={stats.summary.total_cells ?? 0} icon="fa-dna" tone="accent" />
                  <StatCard label="Unique Cell Lines" value={stats.summary.unique_cell_lines ?? 0} icon="fa-layer-group" tone="primary" />
                </Box>
                {Array.isArray(stats.by_origin) && stats.by_origin.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>By Origin</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {stats.by_origin.map((o, i) => (
                        <Typography
                          key={i}
                          variant="caption"
                          sx={{ bgcolor: 'var(--color-primary-container)', px: 1, py: 0.25, borderRadius: 1 }}
                        >
                          {o.origin}: {o.count}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
            {remainingSpaceData.length === 0 ? (
              <Typography variant="subtitle1" textAlign="center" sx={{ mt: 2 }}>
                No cryopreservation boxes found with vials.
              </Typography>
            ) : (
              <TableContainer component={Paper} elevation={1}>
                <Table size="small">
                  <TableHead
                    sx={{ bgcolor: 'var(--color-primary-container)' }}
                  >
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Box Number</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Vials Used</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Remaining Space</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {remainingSpaceData.map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{item.box_number}</TableCell>
                        <TableCell>{item.vials_used}</TableCell>
                        <TableCell>{item.remaining_space}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onRefresh} 
          color="primary" 
          variant="outlined" 
          sx={{ fontWeight: 600, borderRadius: 2, borderWidth: 2, width: 180, mr: 1 }}
          startIcon={<i className="fas fa-sync"></i>}
          disabled={loading}
        >
          Refresh
        </Button>
        <Button onClick={onClose} color="primary" variant="contained" sx={{ fontWeight: 600, borderRadius: 2, width: 180 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RemainingSpaceModal; 